import type * as React from "react";
import { type FC, type ReactNode, useContext, useMemo, useState } from "react";
import classNames from "classnames";
import { MaterialInput } from "../../common/components/materialInput/MaterialInput";
import GridGappedContainer, {
  GridGappedContainerSize,
} from "../../common/components/GridGappedContainer/GridGappedContainer";
import { IconClickable } from "../../common/components/icon/IconClickable";
import { IconType } from "../../common/components/icon/IconType";
import Checkbox from "../../common/components/Checkbox";
import ProductImage from "../../common/components/Product/ProductImage";
import ProductDesignation from "../../ProductDetails/components/ProductDesignation";
import { GET_LINEAR_CONFIGURATOR_URL_FOR_ITEM } from "../Linear/constants";
import { type ProductData } from "../../models/ProductData";
import { DefaultConfirmationContext } from "../Linear/components/ConfirmationContext";
import { SavedConfigContext, type SavedConfigurationItem } from "./SavedConfigurationsPage";
import { CommentButton } from "../../QuoteCart/components/ActiveQuoteEdit/ProductTable/CommentButton";
import { Row } from "../../common/components/Layout";
import LanguageAwareExternalContent from "../Linear/components/LanguageAwareExternalContent";
import { userIsCustomer } from "../../utils/roles";
import { Card } from "../Linear/components/Card";
import { canSeeSalesFinder } from "../../Header/components/headerMenu/DesktopPersonalNav";
import { CART_PRODUCT_IMAGE_WIDTHS } from "../../Cart/components/CartPage/CartEntryProductInfo";
import { ImageFormat, ImageType } from "../../dto/ProductDataDTO";
import { resolveUrl } from "../../utils/url/urlResolver";
import ShareConfigurationsMenu from "./ShareConfigurationsMenu";
import ShareConfigurationsViaEmailPopup from "./ShareConfigurationsViaEmailPopup";
import { useTranslation } from "react-i18next";

import classes from "./SavedConfigurationsTableEntry.module.less";
import { Stacker } from "~/common/components/Stacker/Stacker";
import { CommentInput, useShowAndScrollInput } from "~/common/components/CommentInput/CommentInput";
import { useForcedConfiguratorTranslations } from "~/Configurators/common/useXmlConfiguratorTranslations";
import { ConfiguratorType, getConfiguratorTypeFromProductCode } from "~/Configurators/ConfiguratorType";
import { Button } from "~/common/components/Button/Button";

interface Props {
  index: number;
  item: SavedConfigurationItem;
}

const IS_ALT_EXTERNAL_CONTENT = /^[a-zA-Z0-9_-]+$/;

const SavedConfigurationsTableEntry: FC<Props> = ({ index, item }) => {
  const { t } = useTranslation();

  const [name, setName] = useState(item.name);
  const [comment, setComment] = useState(item.comment);
  const contextData = useContext(SavedConfigContext);

  const code = item.code;

  const isEdited = item.name !== name;
  const saveItem = () => contextData.updateItem(code, name, comment);
  const deleteItem = () => contextData.deleteItem(code);
  const selectItem = (newVal: boolean) => contextData.selectItem(code, newVal);

  const updateComment = (newComment: string) => {
    contextData.updateItem(code, name, newComment);
  };

  const updateShowComment = (newValue: boolean) => {
    contextData.toggleComment(code, newValue);
  };

  const shareConfigurationsViaSalesPartnerFinder = (items: SavedConfigurationItem[]) => {
    contextData.shareItemsViaSalesPartnerFinder(items);
  };

  const configuratorType = getConfiguratorTypeFromProductCode(item.schaefflerId) ?? ConfiguratorType.MetricShaft;

  const url = GET_LINEAR_CONFIGURATOR_URL_FOR_ITEM(item.code, item.importUrlPrefix);

  const isCustomer = userIsCustomer();

  const [shareConfigurationsMenuAnchorEl, setShareConfigurationsMenuAnchorEl] = useState<Element | null>(null);
  const [isShareConfigurationsMenuOpen, setIsShareConfigurationsMenuOpen] = useState(false);
  const [isShareConfigurationsViaEmailPopupOpen, setIsShareConfigurationsViaEmailPopupOpen] = useState(false);

  // TODO - Missing configurator type
  const xmlT = useForcedConfiguratorTranslations(item.schaefflerId).t;

  const product: Pick<
    ProductData,
    "images" | "manufacturerLogoUrl" | "brandLogoUrl" | "url" | "isOptime" | "sapMaterialNumber" | "code"
  > = useMemo(() => {
    return {
      images: [
        {
          imageType: ImageType.primary,
          url: item.useXmlTranslation && xmlT ? xmlT(item.mediaUrl) : item.mediaUrl,
          altText: "",
          format: ImageFormat.thumbnail,
        },
      ],
      manufacturerLogoUrl: item.manufacturerUrl || undefined,
      brandLogoUrl: undefined,
      url,
      isOptime: false,
      sapMaterialNumber: "",
      code: item.schaefflerId,
    };
  }, [item.manufacturerUrl, item.mediaUrl, url]);

  const [commentRef, toggleComment] = useShowAndScrollInput(() => updateShowComment(!item.showComment));

  const shortDescription = item.shortDescription ?? "";
  let descriptionProps;
  if (shortDescription.includes("http")) {
    descriptionProps = {
      content: shortDescription,
    };
  } else if (IS_ALT_EXTERNAL_CONTENT.test(shortDescription)) {
    const url = `https://cdn.schaeffler-ecommerce.com/frontend/linear/${shortDescription}_en-GB.html`;
    descriptionProps = {
      content: url,
    };
  } else {
    descriptionProps = {
      forceXmlConfiguratorCode: configuratorType !== ConfiguratorType.System ? item.schaefflerId : undefined,
      content: shortDescription,
    };
  }

  return (
    <div className={"scha__lcsc__table__entry"}>
      <div className={"scha__lcsc__table__left-side scha__lcsc__table__left-side__col-container"}>
        <div>{index}</div>

        <div className={"d-flex mx-3"}>
          <div className={classes.imageContainer}>
            <ProductImage className={"mb-3 mr-3"} product={product} widths={CART_PRODUCT_IMAGE_WIDTHS} />
          </div>

          <Stacker gap={10}>
            <a href={resolveUrl(url)}>
              <ProductDesignation
                size={"title-medium"}
                designation={item.designation}
                isPreferred={false}
                tooltipSide={"bottom"}
              />
            </a>

            <p className="mt-1 mb-0">
              <LanguageAwareExternalContent {...descriptionProps} loadedClassname={"scha__lc__external-label"} />
            </p>
          </Stacker>
        </div>
      </div>

      <div className={"scha__lcsc__table__right-side"}>
        <Card className={"py-3"} innerClassName={"scha__lcsc__table__right-side__col-container"} disableHover={true}>
          <div className={classes.cardInner}>
            <div className={classes.firstRow}>
              <div className={classes.cardLeftSide}>
                <Cell>
                  <div className={"d-flex flex-align-baseline w-100"}>
                    <MaterialInput
                      label={t("page.savedLinearConfigurations.table.header.name")}
                      onChange={(e) => setName(e.target.value)}
                      value={name}
                    />
                    {isEdited && (
                      <div className={"ml-3"}>
                        <Button size={"medium"} color={"secondary"} onClick={saveItem} disabled={item.isBeingUpdated}>
                          {t("generic.save")}
                        </Button>
                      </div>
                    )}
                  </div>
                </Cell>
              </div>
              <div className={classes.icons}>
                <Cell justifyEnd={true}>
                  <GridGappedContainer gapSize={GridGappedContainerSize.S_16}>
                    {canSeeSalesFinder() && !isCustomer && (
                      <>
                        <IconClickable
                          className={"scha_wishlist_deleteitem"}
                          type={IconType.Share}
                          disabled={item.isBeingDeleted}
                          onClick={(e) => {
                            setShareConfigurationsMenuAnchorEl(e.currentTarget);
                            setIsShareConfigurationsMenuOpen(true);
                          }}
                        />
                        <ShareConfigurationsMenu
                          anchorEl={shareConfigurationsMenuAnchorEl}
                          isOpen={isShareConfigurationsMenuOpen}
                          onClose={() => setIsShareConfigurationsMenuOpen(false)}
                          shareConfigurationsViaEmail={() => {
                            setIsShareConfigurationsMenuOpen(false);
                            setIsShareConfigurationsViaEmailPopupOpen(true);
                          }}
                          shareConfigurationsViaSalesPartnerFinder={() => {
                            setIsShareConfigurationsMenuOpen(false);
                            shareConfigurationsViaSalesPartnerFinder([item]);
                          }}
                        />
                        {isShareConfigurationsViaEmailPopupOpen && (
                          <ShareConfigurationsViaEmailPopup
                            onClose={() => setIsShareConfigurationsViaEmailPopupOpen(false)}
                            items={[item]}
                            onSuccess={() => setIsShareConfigurationsViaEmailPopupOpen(false)}
                          />
                        )}
                      </>
                    )}
                    {isCustomer && (
                      <>
                        <IconClickable
                          className={"scha_wishlist_deleteitem"}
                          type={IconType.Share}
                          disabled={item.isBeingDeleted}
                          onClick={(e) => {
                            setIsShareConfigurationsViaEmailPopupOpen(true);
                          }}
                        />
                        {isShareConfigurationsViaEmailPopupOpen && (
                          <ShareConfigurationsViaEmailPopup
                            onClose={() => setIsShareConfigurationsViaEmailPopupOpen(false)}
                            items={[item]}
                            onSuccess={() => setIsShareConfigurationsViaEmailPopupOpen(false)}
                          />
                        )}
                      </>
                    )}
                    <CommentButton
                      onClick={toggleComment}
                      open={item.showComment}
                      fontSize={"small"}
                      className={"scha__saved-configurations__comment-button"}
                    />
                    <DefaultConfirmationContext onConfirm={deleteItem}>
                      {(openConfirmation) => (
                        <IconClickable
                          onClick={openConfirmation}
                          className={"scha_wishlist_deleteitem"}
                          type={IconType.Bin}
                          disabled={item.isBeingDeleted}
                        />
                      )}
                    </DefaultConfirmationContext>

                    <Checkbox
                      checked={item.isSelected}
                      className={"scha_wishlist_checkbox"}
                      id={"wishlist_small_icon_checkbox"}
                      onCheck={selectItem}
                    />
                  </GridGappedContainer>
                </Cell>
              </div>
            </div>
            <div className={classes.commentMargin}>
              <Row className={"mt-3"}>
                <div className={classes.comment}>
                  {item.showComment && (
                    <CommentInput
                      value={item.comment}
                      onChange={updateComment}
                      ref={commentRef}
                      label={t("wishlistPage.comment.label")}
                    />
                  )}
                </div>
              </Row>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};

const Cell: React.FC<{ justifyEnd?: boolean; children?: ReactNode }> = ({ children, justifyEnd }) => {
  const cls = classNames("px-3 d-flex flex-align-start", justifyEnd && "flex-justify-end");
  return <div className={cls}>{children}</div>;
};

export default SavedConfigurationsTableEntry;
